import React, { useRef, useState } from 'react';

import { EyeIcon, UploadIcon, InfoIcon } from 'lucide-react';

import ImageViewer from '../../ui-components/ImageViewer';

import type { FileUploadFieldProps } from '../settings/model';

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  label,
  value,
  onUpload,
  accept = '',
  helpText,
  fileName,
  infoText,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
    }
  };

  const handleUploadClick = () => fileInputRef.current?.click();
  const handlePreviewClick = () => setIsViewerOpen(true);

  const isImage = /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(value ?? '');

  return (
    <div className="rounded-lg border border-gray-200 h-max bg-white shadow w-full max-w-sm mx-auto overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
        <span className="text-sm font-semibold text-gray-700">{label}</span>
        {infoText && (
          <div className="relative group">
            <InfoIcon size={16} className="text-gray-400 cursor-pointer" />
            <span
              className="
             absolute bottom-full left-1/2 -translate-x-1/2 mb-2
             text-xs bg-black text-white rounded px-2 py-1
             opacity-0 group-hover:opacity-100 transition whitespace-nowrap
           "
            >
              {infoText}
            </span>
          </div>
        )}
      </div>

      {fileName && (
        <div className="px-4 py-2 text-xs text-brand-600 border-b truncate">
          {fileName}
        </div>
      )}

      <div className="flex items-center justify-center min-h-[100px] py-4 px-4 bg-white">
        {isImage ? (
          <img
            src={value}
            alt={label}
            className="max-h-[100px] object-contain"
          />
        ) : (
          <span className="text-sm text-gray-400">No image available</span>
        )}
      </div>

      <div className="flex justify-center gap-4 border-t px-4 py-3 bg-gray-50">
        <input
          type="file"
          accept={accept}
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
        />
        <div className="relative group">
          <button
            type="button"
            onClick={handleUploadClick}
            className="bg-error-500 hover:bg-error-600 text-white cursor-pointer p-2 rounded transition"
          >
            <UploadIcon />
          </button>
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 text-xs bg-black text-white rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition whitespace-nowrap">
            Upload
          </span>
        </div>
        {value && isImage && (
          <div className="relative group">
            <button
              type="button"
              onClick={handlePreviewClick}
              className="bg-brand-500 hover:bg-brand-600 text-white p-2 cursor-pointer rounded transition"
            >
              <EyeIcon />
            </button>
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 text-xs bg-black text-white rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition">
              Preview
            </span>
          </div>
        )}
      </div>

      {helpText && (
        <p className="px-4 pb-3 text-xs text-gray-500">{helpText}</p>
      )}

      {isViewerOpen && value && (
        <ImageViewer
          src={value}
          trigger={true}
          onClose={() => setIsViewerOpen(false)}
        />
      )}
    </div>
  );
};

export default FileUploadField;
